import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/cookieConsent.css';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentProps {
  onPreferencesChange: (preferences: CookiePreferences) => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onPreferencesChange }) => {
  const [showModal, setShowModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always required
    analytics: false, // Default OFF for GDPR/privacy compliance
    marketing: false,  // Default OFF for GDPR/privacy compliance
  });

  // Check if user has already set cookie preferences
  useEffect(() => {
    const savedPreferences = localStorage.getItem('cookiePreferences');
    
    if (!savedPreferences) {
      // If no preferences are saved, show the modal
      setShowModal(true);
    } else {
      // Apply saved preferences
      const parsedPreferences = JSON.parse(savedPreferences) as CookiePreferences;
      setPreferences(parsedPreferences);
      onPreferencesChange(parsedPreferences);
    }
  }, [onPreferencesChange]);

  const savePreferences = (newPreferences: CookiePreferences) => {
    // Save to localStorage
    localStorage.setItem('cookiePreferences', JSON.stringify(newPreferences));
    
    // Update state
    setPreferences(newPreferences);
    
    // Close modal and settings
    setShowModal(false);
    setShowSettings(false);
    
    // Notify parent component
    onPreferencesChange(newPreferences);
  };

  const handleAcceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleSaveSettings = () => {
    savePreferences(preferences);
  };

  const toggleSetting = (setting: keyof CookiePreferences) => {
    if (setting === 'necessary') return; // Can't toggle necessary cookies
    
    setPreferences({
      ...preferences,
      [setting]: !preferences[setting],
    });
  };

  // Handle "Manage Settings" button click
  const handleManageSettings = () => {
    setShowSettings(true);
  };

  // Handle privacy policy link click without dismissing the banner
  const handlePrivacyPolicyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open('/privacy', '_blank');
    return false;
  };

  return (
    <>
      {showModal && (
        <div className="cookieConsentOverlay">
          <div className="cookieConsentModal">
            <h3>Cookie Consent</h3>
            <p>
              We use cookies to enhance your browsing experience, serve personalized ads or content, 
              and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
              For more information about how we use cookies, please read our{' '}
              <a href="/privacy" className="policyLink" onClick={handlePrivacyPolicyClick}>
                Privacy Policy
              </a>.
            </p>
            
            <div className="cookieConsentButtons">
              <button 
                className="primaryButton" 
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
              <button 
                className="secondaryButton" 
                onClick={handleManageSettings}
              >
                Manage Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="cookieSettingsOverlay">
          <div className="cookieSettingsModal">
            <h3>Cookie Settings</h3>
            <p>
              Customize your cookie preferences below. For detailed information about the cookies we use and how we handle your data, please see our{' '}
              <a href="/privacy" className="policyLink" onClick={handlePrivacyPolicyClick}>
                Privacy Policy
              </a>.
            </p>

            <div className="cookieSettingsList">
              <div className="cookieSettingItem">
                <div className="cookieSettingHeader">
                  <span className="cookieSettingTitle">Necessary Cookies</span>
                  <div className={`cookieToggle enabled`}>
                    <div className="cookieToggleSlider"></div>
                  </div>
                </div>
                <p className="cookieSettingDescription">
                  These cookies are essential for the website to function and cannot be disabled.
                </p>
              </div>

              <div className="cookieSettingItem">
                <div className="cookieSettingHeader">
                  <span className="cookieSettingTitle">Analytics Cookies</span>
                  <div 
                    className={`cookieToggle ${preferences.analytics ? 'enabled' : ''}`}
                    onClick={() => toggleSetting('analytics')}
                  >
                    <div className="cookieToggleSlider"></div>
                  </div>
                </div>
                <p className="cookieSettingDescription">
                  These cookies help us understand how visitors interact with our website through 
                  Google Analytics, providing insights to improve our content and user experience.
                </p>
              </div>

              <div className="cookieSettingItem">
                <div className="cookieSettingHeader">
                  <span className="cookieSettingTitle">Marketing Cookies</span>
                  <div 
                    className={`cookieToggle ${preferences.marketing ? 'enabled' : ''}`}
                    onClick={() => toggleSetting('marketing')}
                  >
                    <div className="cookieToggleSlider"></div>
                  </div>
                </div>
                <p className="cookieSettingDescription">
                  These cookies are used to track activity across websites to deliver personalized ads relevant to your interests.
                </p>
              </div>
            </div>

            <div className="cookieSettingsButtons">
              <button className="primaryButton" onClick={handleSaveSettings}>
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent; 